import React, { useEffect, useState } from 'react';
import { Button, Card, DatePicker, Form, Input, message, Select, Table, Tag } from 'antd';
import { CheckCircleOutlined, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import api from '../api/axios';

const { RangePicker } = DatePicker;

const statusOptions = [
  { value: '', label: '全部状态' },
  { value: 'pending', label: '待确认' },
  { value: 'confirmed', label: '已确认' }
];

const getApiBaseUrl = () => (
  process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5001/api'
);

const AlarmDataPage = ({ scope = 'admin' }) => {
  const [alarms, setAlarms] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [filters, setFilters] = useState({});
  const [form] = Form.useForm();

  const endpoint = scope === 'admin' ? '/alarms/admin' : '/alarms/school';
  const tokenKey = scope === 'admin' ? 'token' : 'school_token';
  const title = scope === 'admin' ? '总后台报警数据' : '学校报警数据';

  const fetchAlarms = async (page = pagination.current, pageSize = pagination.pageSize, nextFilters = filters) => {
    setLoading(true);
    try {
      const params = {
        page,
        pageSize,
        ...nextFilters
      };
      const response = await api.get(endpoint, { params });
      setAlarms(response.data.data || []);
      setTotal(response.data.total || 0);
      setPagination({ current: page, pageSize });
    } catch (error) {
      message.error('获取报警数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlarms(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  useEffect(() => {
    const token = localStorage.getItem(tokenKey);
    if (!token || typeof EventSource === 'undefined') return undefined;

    const source = new EventSource(`${getApiBaseUrl()}/alarms/stream?token=${encodeURIComponent(token)}`);
    const handleNewAlarm = (event) => {
      const alarm = JSON.parse(event.data);
      setAlarms(prev => [alarm, ...prev].slice(0, pagination.pageSize));
      setTotal(prev => prev + 1);
      message.warning(`收到新的学生报警：${alarm.student_name || '未知学生'}`);
    };
    const handleConfirmedAlarm = (event) => {
      const alarm = JSON.parse(event.data);
      setAlarms(prev => prev.map(item => item.id === alarm.id ? alarm : item));
    };

    source.addEventListener('alarm:new', handleNewAlarm);
    source.addEventListener('alarm:confirmed', handleConfirmedAlarm);
    source.onerror = () => {
      source.close();
    };

    return () => {
      source.close();
    };
  }, [scope, tokenKey, pagination.pageSize]);

  const handleSearch = (values) => {
    const nextFilters = {
      studentName: values.studentName || undefined,
      status: values.status || undefined,
      schoolId: scope === 'admin' ? values.schoolId || undefined : undefined,
      classId: values.classId || undefined
    };
    if (values.timeRange?.length === 2) {
      nextFilters.startTime = values.timeRange[0].startOf('day').format('YYYY-MM-DD HH:mm:ss');
      nextFilters.endTime = values.timeRange[1].endOf('day').format('YYYY-MM-DD HH:mm:ss');
    }
    setFilters(nextFilters);
    fetchAlarms(1, pagination.pageSize, nextFilters);
  };

  const handleConfirm = async (alarm) => {
    try {
      const confirmEndpoint = scope === 'admin'
        ? `/alarms/admin/${alarm.id}/confirm`
        : `/alarms/school/${alarm.id}/confirm`;
      const response = await api.put(confirmEndpoint, {});
      setAlarms(prev => prev.map(item => item.id === alarm.id ? response.data : item));
      message.success('报警已确认');
    } catch (error) {
      message.error(error.response?.data?.message || '确认失败');
    }
  };

  const columns = [
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: status => (
        <Tag color={status === 'confirmed' ? 'green' : 'red'}>
          {status === 'confirmed' ? '已确认' : '待确认'}
        </Tag>
      )
    },
    { title: '学生', dataIndex: 'student_name', width: 120 },
    { title: '学校', dataIndex: 'school_name', width: 180 },
    { title: '班级', dataIndex: 'class_name', width: 160 },
    { title: '设备', dataIndex: 'device_name', width: 160 },
    {
      title: '报警时间',
      dataIndex: 'alarm_time',
      width: 180,
      render: time => time ? new Date(time).toLocaleString() : ''
    },
    { title: '确认人', dataIndex: 'confirmed_by_name', width: 120, render: value => value || '-' },
    {
      title: '确认时间',
      dataIndex: 'confirmed_at',
      width: 180,
      render: time => time ? new Date(time).toLocaleString() : '-'
    },
    {
      title: '操作',
      fixed: 'right',
      width: 110,
      render: (_, alarm) => (
        alarm.status === 'pending' ? (
          <Button type="link" icon={<CheckCircleOutlined />} onClick={() => handleConfirm(alarm)}>
            确认
          </Button>
        ) : '-'
      )
    }
  ];

  return (
    <Card
      title={<span><WarningOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />{title}</span>}
      extra={<Button icon={<ReloadOutlined />} onClick={() => fetchAlarms()} loading={loading}>刷新</Button>}
    >
      <Form form={form} layout="inline" onFinish={handleSearch} style={{ marginBottom: 16, rowGap: 12 }}>
        {scope === 'admin' && (
          <Form.Item name="schoolId" label="学校ID">
            <Input allowClear placeholder="学校ID" style={{ width: 120 }} />
          </Form.Item>
        )}
        <Form.Item name="classId" label="班级ID">
          <Input allowClear placeholder="班级ID" style={{ width: 120 }} />
        </Form.Item>
        <Form.Item name="studentName" label="学生姓名">
          <Input allowClear placeholder="学生姓名" style={{ width: 160 }} />
        </Form.Item>
        <Form.Item name="status" label="状态" initialValue="">
          <Select options={statusOptions} style={{ width: 120 }} />
        </Form.Item>
        <Form.Item name="timeRange" label="报警日期">
          <RangePicker />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit">查询</Button>
        </Form.Item>
        <Form.Item>
          <Button onClick={() => {
            form.resetFields();
            form.setFieldsValue({ status: '' });
            setFilters({});
            fetchAlarms(1, pagination.pageSize, {});
          }}>
            重置
          </Button>
        </Form.Item>
      </Form>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={alarms}
        loading={loading}
        scroll={{ x: 1300 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total,
          showSizeChanger: true,
          showTotal: count => `共 ${count} 条`,
          onChange: (page, pageSize) => fetchAlarms(page, pageSize)
        }}
      />
    </Card>
  );
};

export default AlarmDataPage;
